import axios from 'axios';

import { apiClient } from './apiClient';

export type AccountAuditStatus = 'pass' | 'warning' | 'fail';
export type AccountAuditSeverity = 'info' | 'warning' | 'blocking';
export type AccountAuditActionScope = 'read_only' | 'approval_required' | 'operator_only';

export interface AccountAuditFinding {
  id: string;
  category: string;
  title: string;
  status: AccountAuditStatus;
  severity: AccountAuditSeverity;
  evidence: string;
  recommendation: string;
  action_scope: AccountAuditActionScope;
  action_label?: string | null;
  action_url?: string | null;
}

export interface AccountAuditSummary {
  passed: number;
  warnings: number;
  failed: number;
  blocking: number;
  /**
   * Rows that could actually serve and were audited, vs rows dropped because a
   * campaign, ad group or the entity itself is paused or removed.
   *
   * The serving fixes remove 85-91% of the rows these reads used to return, so
   * without this the finding count collapses with nothing on the page saying
   * why — which reads as a broken tool rather than a fixed one. Optional
   * because a cached run persisted before this shipped will not carry them.
   */
  rows_audited?: number;
  rows_excluded?: number;
  scope_note?: string;
}

export interface AccountAuditResponse {
  run_id?: string | null;
  tenant_id: string;
  customer_id: string | null;
  connected: boolean;
  generated_at: string;
  summary: AccountAuditSummary;
  findings: AccountAuditFinding[];
}

export async function runAccountAudit(
  tenantId: string,
): Promise<AccountAuditResponse> {
  const { data } = await apiClient.post<AccountAuditResponse>(
    '/google-ads/account-audit/run',
    undefined,
    { params: { tenant_id: tenantId } },
  );
  return data;
}

export async function getLatestAccountAudit(
  tenantId: string,
): Promise<AccountAuditResponse | null> {
  try {
    const { data } = await apiClient.get<AccountAuditResponse>(
      '/google-ads/account-audit/latest',
      { params: { tenant_id: tenantId } },
    );
    return data;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      return null;
    }
    throw err;
  }
}
